<script setup lang="ts">
import { useUserStore } from '@/stores/modules/user'
import type { NestMonsterType } from '@/types/monster'
import { socketEmitAsync } from '@/utils/web-socket'
import { QuestionFilled } from '@element-plus/icons-vue'
import { ref } from 'vue'
import { useRoomStore } from '@/stores/modules/room'
import CrackMonsterDetail from '@/components/crack-monster-detail/CrackMonsterDetail.vue'
import { crackRank } from '../data'
import { onMounted } from 'vue'

const envMode = import.meta.env.VITE_ENV_MODE

const userStore = useUserStore()
const roomStore = useRoomStore()

defineProps<{ crackMonster?: NestMonsterType }>()
const emits = defineEmits(['getMonster', 'start', 'giveUp', 'showRoom'])

const rank = ref(1)
const localKey = `war-soul-${envMode === 'test' ? 'test-' : ''}crack-rank`
function handleChangeRank() {
  localStorage.setItem(localKey, String(rank.value))
}
function handleHuntCrackMonster() {
  if (userStore.crackTimes <= 0 && userStore.consumables.crackFight <= 0) return ElMessage.warning('无次数或挑战券时无法进行探索')

  ElMessageBox.confirm(
    `<p class="affix">确定<span class="info">探索</span>【<span class="peerless">虚空裂隙</span>】吗？（本次探索的难度为<span class="${
      crackRank[rank.value].class
    }">${crackRank[rank.value].name}</span>）</p>`,
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(async () => {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'huntCrackMonster', data: { rank: rank.value } })
    emits('getMonster', res)
    ElMessage.success('探索成功')
  })
}
async function handleStartCrack() {
  if (roomStore.id) return start()
  ElMessageBox.confirm(
    '<p class="affix">确定<span class="hp">独自</span>进行【<span class="peerless">虚空裂隙</span>】<span class="info">战斗</span>吗？</p>',
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(() => start())

  async function start() {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'startCrackFight' })
    roomShow.value = false
    roomStore.clearRoomInfo()
    emits('start', { ...res, type: 'crack' })
  }
}
function handleRefreshCrack() {
  if (userStore.gold < 100000) return ElMessage.warning('重新探索需要 100,000金币')

  if (roomStore.id) return ElMessage.warning('无法在房间内时重新探索魔物')
  ElMessageBox.confirm(
    `<p class="affix">确定<span class="hp">放弃</span>探索当前裂隙并进行重新<span class="info">探索</span>吗？（消耗 <span class="gold">100,000</span>金币，本次探索的难度为<span class="${
      crackRank[rank.value].class
    }">${crackRank[rank.value].name}</span>）</p>`,
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(async () => {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'refreshCrackMonster', data: { rank: rank.value } })
    userStore.gold -= 100000
    emits('getMonster', res)
    ElMessage.success('重新探索成功')
  })
}

const roomShow = ref(false)
function handleOpenRoom() {
  if (userStore.checkRuneCapacity) return ElMessage.warning('符石背包已满，无法进行战斗')
  emits('showRoom')
}

onMounted(() => {
  const localRank = localStorage.getItem(localKey)
  if (localRank) rank.value = Number(localRank)
})
</script>

<template>
  <div class="border-wrap crack">
    <h4 class="peerless">
      虚空裂隙
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix" style="max-height: 60vh; overflow: auto">
            <h4>虚空裂隙说明</h4>
            <p>1. 虚空裂隙<span class="info">每周日0点刷新</span>，刷新后将重置奖励次数。</p>
            <p>
              2. 通过<span class="info">探索</span>，您会随机遭遇一只<span class="info">虚空怪物</span>，同时得知<span class="diamond">报酬</span>。
            </p>
            <p>
              3. 通过虚空裂隙战斗您将必定获得 对应报酬的 <span class="diamond">钻石</span> 及
              <span class="peerless">裂隙结晶</span>，并有概率获得<span class="peerless">绝世</span>装备。
            </p>
            <p>
              4. <span class="info">难度</span>越高的怪物挑战难度越大，同时随难度提升，怪物具备的<span class="info">虚化值</span
              >也将越高，若您未拥有足够的<span class="info">抗魔值</span>，将受到大幅度的伤害削弱。
            </p>
            <p>5. 虚空裂隙支持<span class="info">双人组队</span>，当组队挑战时，怪物的血量增加<span class="grow">0.7</span>倍。</p>
            <p>6. 战斗时支持<span class="info">切换装备即刻生效</span>，当输出不足时，请及时更换装备。</p>
            <p>7. 战斗失败时，<span class="grow">不会消耗</span>奖励次数；当奖励次数用尽时，您无法进行探索及获得奖励，但您仍可以帮助其他玩家战斗。</p>
            <p>8. 副本各项信息如表格所示：</p>
            <table border style="width: 100%; text-align: center">
              <tr>
                <td>参考信息</td>
                <td class="normal">简单</td>
                <td class="race">普通</td>
                <td class="epic">困难</td>
                <td class="antiquity">噩梦</td>
              </tr>
              <tr>
                <td>虚化值</td>
                <td>10</td>
                <td>50</td>
                <td>150</td>
                <td>240</td>
              </tr>
              <tr>
                <td>裂隙晶石</td>
                <td>5</td>
                <td>10</td>
                <td>15</td>
                <td>20</td>
              </tr>
              <tr>
                <td>装备爆率</td>
                <td>0%</td>
                <td>15%</td>
                <td>40%</td>
                <td>100%</td>
              </tr>
            </table>
          </div>
        </el-popover>
      </div>
    </h4>
    <p style="display: flex; align-items: center">
      难度：
      <el-select size="small" v-model="rank" :class="crackRank[rank].class" style="width: 90px" @change="handleChangeRank">
        <el-option v-for="item of [1, 2, 3, 4]" :key="item" :label="crackRank[item].name" :value="item">
          <span :class="crackRank[item].class">{{ crackRank[item].name }}</span>
        </el-option>
      </el-select>
    </p>
    <p>
      <span>剩余次数：{{ userStore.crackTimes }}</span>
      <span style="margin-left: 20px">剩余挑战券：{{ userStore.consumables.crackFight }}</span>
    </p>
    <p>
      当前怪物：
      <CrackMonsterDetail :monster="crackMonster" />
    </p>
    <div style="margin: 10px 0">
      <div v-show="!crackMonster?.name">
        <el-button type="warning" plain @click="handleHuntCrackMonster" :disabled="!userStore.soulType">
          {{ !userStore.soulType ? '觉醒后解锁' : '探索' }}
        </el-button>
        <el-button type="primary" plain :disabled="!userStore.soulType" @click="handleOpenRoom">组队战斗</el-button>
      </div>
      <div v-show="crackMonster?.name">
        <el-button type="success" plain @click="() => handleStartCrack()" :disabled="Boolean(roomStore.id)">独自战斗</el-button>
        <el-button type="primary" plain @click="handleOpenRoom">组队战斗</el-button>
        <el-button type="danger" plain @click="handleRefreshCrack">重新探索</el-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep() {
  .normal {
    .el-select__selected-item {
      span {
        color: #888084;
      }
    }
  }
  .race {
    .el-select__selected-item {
      span {
        color: #3e62ed;
      }
    }
  }
  .epic {
    .el-select__selected-item {
      span {
        color: #8d4bbb;
      }
    }
  }
  .antiquity {
    .el-select__selected-item {
      span {
        color: #f05654;
      }
    }
  }
}
</style>
